import { useMemo } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { SelectionList } from "./selections";
import { SkuDetailPanel } from "./selections/detail";
import { StaticSkuDetailPanel } from "./selections/detail/StaticSkuDetailPanel";
import { InfoPanel } from "./info-panel";
import { ChildTemplatesPanel } from "./child-templates";
import { SelectionDetailPanel } from "../../components/SelectionDetailPanel";
import { useConfigStore } from "../../store/configStore";
import { useEditorStore } from "../../store/editorStore";
import { Badge } from "@/components/ui/badge";
import { Layers, Info, GitBranch } from "lucide-react";
import {
  SpineTemplate,
  isChildSpineTemplate,
} from "../../types/spine-template";

/** 静态层模版：仅 Info + Selection，与完整 Spine 编辑区分 */
export function StaticConfigPanel() {
  const { tid } = useParams<{ tid: string }>();
  const { data: template } = useSWR<SpineTemplate>(
    tid ? [`/spine-templates/${tid}`] : null,
  );
  const showChildrenTab = Boolean(template) && !isChildSpineTemplate(template);
  const templateConfig = useConfigStore((s) => s.templateConfig);
  const activeTab = useEditorStore((s) => s.activeTab);
  const setActiveTab = useEditorStore((s) => s.setActiveTab);
  const editingSkuId = useEditorStore((s) => s.editingSkuId);
  const previewSkinName = useEditorStore((s) => s.previewSkinName);
  const editingSelectionId = useEditorStore((s) => s.editingSelectionId);

  const tabCols = showChildrenTab ? "grid-cols-3" : "grid-cols-2";
  const resolvedTab =
    !showChildrenTab && activeTab === "children" ? "info" : activeTab;

  const detailPanel = useMemo(() => {
    if (
      editingSkuId &&
      templateConfig?.skus?.[editingSkuId]?.category === "static"
    ) {
      return <StaticSkuDetailPanel />;
    }
    if (editingSkuId || previewSkinName) {
      return <SkuDetailPanel />;
    }
    if (editingSelectionId) {
      return <SelectionDetailPanel />;
    }
    return null;
  }, [
    editingSkuId,
    previewSkinName,
    editingSelectionId,
    templateConfig?.skus,
  ]);

  const selectionCount = templateConfig?.selection.length || 0;

  return (
    <Tabs
      value={resolvedTab}
      onValueChange={setActiveTab}
      className="h-full flex flex-col gap-4"
    >
      <TabsList className={`grid w-full h-auto ${tabCols}`}>
        <TabsTrigger value="info" className="gap-1 text-xs px-3 py-1.5">
          <Info className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Info</span>
        </TabsTrigger>
        <TabsTrigger value="selection" className="gap-1 text-xs px-3 py-1.5">
          <Layers className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Selection</span>
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
            {selectionCount}
          </Badge>
        </TabsTrigger>
        {showChildrenTab && (
          <TabsTrigger value="children" className="gap-1 text-xs px-3 py-1.5">
            <GitBranch className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">子模版</span>
          </TabsTrigger>
        )}
      </TabsList>

      <div className="flex-1 min-h-0 flex gap-4">
        <Card className="flex-1 flex flex-col min-w-0">
          <CardContent className="flex-1 min-h-0 p-4 overflow-y-auto">
            <TabsContent value="info" className="mt-0">
              <InfoPanel />
            </TabsContent>

            <TabsContent value="selection" className="mt-0">
              <SelectionList />
            </TabsContent>

            {showChildrenTab && (
              <TabsContent value="children" className="mt-0">
                <ChildTemplatesPanel />
              </TabsContent>
            )}
          </CardContent>
        </Card>

        {detailPanel && <div className="flex-1 min-w-0">{detailPanel}</div>}
      </div>
    </Tabs>
  );
}
